import { Box, Collapse, IconButton, Typography } from "@mui/material"
import {
  AddOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { PurchaseCostPackageLabel, type PurchasingCostsByWarehouse } from "../../types"

import { formatWarehouseLabel } from "./purchaseCostLabels"
import { PurchaseCostPackageCard } from "./PurchaseCostPackageCard"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { PurchasingCostListEntry } from "@/graphql/codegen/graphql"

interface PurchaseCostsWarehouseSectionProps {
  warehouse: PurchasingCostsByWarehouse
  canEdit: boolean
  onEditPackage: (entry: PurchasingCostListEntry) => void
  onAddFuture: (warehouseAreaId: string) => void
  onShowHistory: (warehouseAreaId: string) => void
}

export const PurchaseCostsWarehouseSection: FC<PurchaseCostsWarehouseSectionProps> = ({
  warehouse,
  canEdit,
  onEditPackage,
  onAddFuture,
  onShowHistory,
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  const hasPackages = warehouse.current.length > 0 || warehouse.future.length > 0
  const testIdRoot = `product.purchase-costs.warehouse-section-${warehouse.warehouseAreaId}`

  return (
    <Box className='flex flex-col rounded border border-gray-400' data-testid={testIdRoot}>
      <Box className='flex items-center justify-between bg-primary-100 p-4'>
        <Typography variant='body2' className='text-gray-700'>
          {formatWarehouseLabel(warehouse.location)}
        </Typography>
        <IconButton
          size='small'
          onClick={() => setIsExpanded((prev) => !prev)}
          data-testid={`${testIdRoot}.toggle-button`}
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
        >
          {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded} unmountOnExit>
        <Box className='flex flex-col'>
          {hasPackages ? (
            <Box className='p-6'>
              <Box className='flex items-stretch overflow-x-auto rounded border border-gray-400'>
                {warehouse.current.map((pkg) => (
                  <PurchaseCostPackageCard
                    key={pkg.id}
                    package={pkg}
                    label={PurchaseCostPackageLabel.Current}
                    onEdit={canEdit ? onEditPackage : undefined}
                  />
                ))}
                {warehouse.future.map((pkg) => (
                  <PurchaseCostPackageCard
                    key={pkg.id}
                    package={pkg}
                    label={PurchaseCostPackageLabel.Future}
                    onEdit={canEdit ? onEditPackage : undefined}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box className='p-6'>
              <InformationMissingAlert
                title='No pricing added yet'
                description='Add pricing for this location.'
                action={
                  canEdit && (
                    <Button
                      color='inherit'
                      size='small'
                      appearance='outlined'
                      className='text-primary'
                      startIcon={<AddOutlined />}
                      onClick={() => onAddFuture(warehouse.warehouseAreaId)}
                      data-testid={`${testIdRoot}.add-pricing-button`}
                    >
                      Add Vendor Pricing
                    </Button>
                  )
                }
              />
            </Box>
          )}

          <Box className='flex items-center justify-end gap-x-4 border-t border-gray-400 p-4'>
            <Button
              appearance='text'
              size='small'
              startIcon={<VisibilityOutlined />}
              onClick={() => onShowHistory(warehouse.warehouseAreaId)}
              data-testid={`${testIdRoot}.show-history-button`}
            >
              Show Historical Pricing
            </Button>
            {canEdit && (
              <Button
                appearance='text'
                size='small'
                startIcon={<AddOutlined />}
                onClick={() => onAddFuture(warehouse.warehouseAreaId)}
                data-testid={`${testIdRoot}.add-future-button`}
              >
                Future Pricing
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}
